import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../../shared/grid/usePersistedGridState';
import { IncludeInactiveToggle } from '../../../../shared/components/IncludeInactiveToggle';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../../shared/components/ErrorState';
import { useToast } from '../../../../shared/stores/toast.store';
import { describeApiError } from '../../../../shared/api/errors';
import { tiposHoraHooks } from '../hooks';
import { tiposHoraApi } from '../api';
import { TipoHoraFormDialog } from '../TipoHoraFormDialog';
import { HORA_TIPOS, type TipoHoraDto, type TipoHoraListItem } from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'nome',
  sortOrder: 'asc',
};

const TIPO_LABEL: Record<string, string> = Object.fromEntries(
  HORA_TIPOS.map((t) => [t.value, t.label]),
);

export function TiposHoraListPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('tipos-hora', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<TipoHoraDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<TipoHoraListItem | null>(null);

  const listQuery = tiposHoraHooks.useList(query);
  const removeMutation = tiposHoraHooks.useDelete();

  async function openEdit(row: TipoHoraListItem): Promise<void> {
    try {
      setEditing(await tiposHoraApi.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await removeMutation.mutateAsync(pendingDelete.id);
      toast.success(t('features.tiposHora.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<TipoHoraListItem>[] = [
    { field: 'sigla', headerName: t('features.tiposHora.columns.sigla'), width: 100 },
    { field: 'nome', headerName: t('features.tiposHora.columns.nome'), flex: 1, minWidth: 200 },
    {
      field: 'tipo',
      headerName: t('features.tiposHora.columns.tipo'),
      width: 130,
      valueFormatter: (value: string) => TIPO_LABEL[value] ?? value,
    },
    {
      field: 'multiplicadorHora',
      headerName: t('features.tiposHora.columns.multiplicadorHora'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) =>
        value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }),
    },
    {
      field: 'ativo',
      headerName: t('features.tiposHora.columns.ativo'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t('features.tiposHora.status.active')
              : t('features.tiposHora.status.inactive')
          }
          color={params.value ? 'success' : 'default'}
          size="small"
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.tiposHora.actions.edit', { nome: params.row.nome })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.tiposHora.actions.delete', { nome: params.row.nome })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={t('menu.hourTypes')} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('menu.hourTypes')}
        breadcrumbs={[{ label: t('menu.generalRegistries') }, { label: t('menu.hourTypes') }]}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('features.tiposHora.actions.new')}
          </Button>
        }
      />
      <DataTable<TipoHoraListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.tiposHora.searchPlaceholder')}
        filters={<IncludeInactiveToggle query={query} onChange={setQuery} />}
      />
      <TipoHoraFormDialog
        open={editing !== null}
        tipoHora={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.tiposHora.deleteConfirm.title')}
        description={t('features.tiposHora.deleteConfirm.description', {
          nome: pendingDelete?.nome ?? '',
        })}
        confirmLabel={t('common.delete')}
        loading={removeMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
